import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { FilterChip } from '@/components/ui/Chip';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useStaffTaskEntitySearch,
  useStaffTaskLinkLabels,
  type StaffTaskEntityOption,
} from '@/hooks/useStaffTaskEntitySearch';
import {
  STAFF_TASK_ENTITY_TYPES,
  type StaffTaskEntityType,
  type StaffTaskLink,
} from '@/lib/staffTasks';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function TaskRelationPicker({
  clinicId,
  value,
  onChange,
}: {
  clinicId: string;
  value: StaffTaskLink[];
  onChange: (next: StaffTaskLink[]) => void;
}) {
  const { t } = useTranslation();
  const [entityType, setEntityType] = useState<StaffTaskEntityType>('patient');
  const [query, setQuery] = useState('');
  const search = useStaffTaskEntitySearch(clinicId, entityType, query);
  const labels = useStaffTaskLinkLabels(clinicId, value);
  const selectedKey = new Set(value.map((link) => `${link.entity_type}:${link.entity_id}`));

  const add = (option: StaffTaskEntityOption) => {
    const key = `${option.entity_type}:${option.entity_id}`;
    if (selectedKey.has(key)) return;
    onChange([...value, { entity_type: option.entity_type, entity_id: option.entity_id }]);
    setQuery('');
  };

  const remove = (link: StaffTaskLink) => {
    onChange(
      value.filter(
        (item) => !(item.entity_type === link.entity_type && item.entity_id === link.entity_id),
      ),
    );
  };

  const labelFor = (link: StaffTaskLink) =>
    labels.data?.find(
      (item) => item.entity_type === link.entity_type && item.entity_id === link.entity_id,
    );

  return (
    <div>
      <p className={cn(TYPE.label, 'mb-s2 text-ds-text')}>{t('tasks.field_relations')}</p>
      {value.length > 0 ? (
        <ul className="mb-s3 flex flex-wrap gap-s2">
          {value.map((link) => {
            const meta = labelFor(link);
            return (
              <li
                key={`${link.entity_type}:${link.entity_id}`}
                className="inline-flex items-center gap-s1 rounded-full bg-ds-neutral-tint px-s2 py-1 text-ds-text"
              >
                <span className={TYPE.meta}>
                  {t(`tasks.entity.${link.entity_type}`)}
                  {': '}
                  {meta?.label ?? link.entity_id}
                </span>
                <button
                  type="button"
                  onClick={() => remove(link)}
                  className="text-ds-muted hover:text-ds-danger"
                  aria-label={t('tasks.remove_relation')}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      <div className="mb-s2 flex flex-wrap gap-s2" role="tablist" aria-label={t('tasks.relation_types')}>
        {STAFF_TASK_ENTITY_TYPES.map((type) => (
          <FilterChip
            key={type}
            pressed={entityType === type}
            onClick={() => {
              setEntityType(type);
              setQuery('');
            }}
          >
            {t(`tasks.entity.${type}`)}
          </FilterChip>
        ))}
      </div>
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t('tasks.search_relations')}
        aria-label={t('tasks.search_relations')}
      />
      <div className="mt-s2 max-h-48 overflow-y-auto rounded-ctl border border-ds-line bg-ds-card">
        {search.isLoading ? (
          <div className="space-y-s2 p-s3" aria-busy>
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (search.data ?? []).length === 0 ? (
          <p className={cn(TYPE.small, 'px-s3 py-s3 text-ds-muted')}>{t('tasks.no_relations')}</p>
        ) : (
          <ul>
            {(search.data ?? []).map((option) => {
              const key = `${option.entity_type}:${option.entity_id}`;
              const picked = selectedKey.has(key);
              return (
                <li key={key} className="border-b border-ds-line last:border-b-0">
                  <button
                    type="button"
                    disabled={picked}
                    onClick={() => add(option)}
                    className={cn(
                      'flex w-full flex-col items-start px-s3 py-s2 text-left',
                      picked ? 'text-ds-muted' : 'hover:bg-ds-sunk text-ds-text',
                    )}
                  >
                    <span className={TYPE.body}>{option.label}</span>
                    {option.hint ? (
                      <span className={cn(TYPE.small, 'text-ds-muted')}>{option.hint}</span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
